/**
 * Pure validator for template expressions inside a single text value.
 *
 * Walks every ``{{ … }}`` pair in the text and applies a small set of
 * structural + reference rules. Issues describe what's wrong, where the
 * span sits in the original text (so callers can highlight or jump),
 * and whether the problem should block save (error) or just warn (warning).
 *
 * Kept side-effect-free so both the per-field hook (caret-aware
 * suppression) and the save-time aggregator (graph-wide sweep) can call
 * it on the same value with different contexts.
 *
 * Rule philosophy mirrors the backend's ``_resolve_expression``: anything
 * the resolver would silently turn into ``""`` at runtime is reported here
 * so authors see typos before they bite a production run. The one
 * exception is anything past ``nodes.<validId>.output.<…>`` — the editor
 * has no static schema for non-trigger node outputs yet, so deep paths
 * under ``output`` are intentionally left wide-open.
 */

import type { AutocompleteContext } from './useExpressionAutocomplete'

export type ValidationCode =
  | 'unclosed_braces'
  | 'empty_expression'
  | 'empty_segment'
  | 'unknown_root'
  | 'unknown_function'
  | 'malformed_function_call'
  | 'unknown_node'
  | 'node_not_reachable'
  | 'unknown_trigger_key'
  | 'unknown_variable'

export interface ValidationIssue {
  code: ValidationCode
  /** Translation key under ``workflow.validation.*``. */
  messageKey: string
  /** Template args spliced into the message (already untranslated text). */
  messageArgs?: Record<string, string>
  /** Inclusive start index in the *original* text — points at the opening `{{`. */
  start: number
  /** Exclusive end index — points past the closing `}}` (or text.length when unclosed). */
  end: number
  severity: 'error' | 'warning'
}

/**
 * Context for validation. Extends the autocomplete context with two extras
 * that only matter at save time:
 *
 *  - ``functionNames`` — flat list of known function names. ``null`` means
 *    the registry hasn't loaded (or load failed); function-name checks are
 *    skipped in that case so we don't false-flag.
 *  - ``allNodeIds`` / ``reachableNodeIds`` — when both are passed, an id
 *    that exists but is not reachable becomes ``node_not_reachable``. When
 *    ``allNodeIds`` is undefined we fall back to ``availableNodeIds`` from
 *    the autocomplete context and report the unified ``unknown_node``.
 */
export interface ValidationContext extends AutocompleteContext {
  functionNames: string[] | null
  /** Optional: every node id in the workflow (save-time only). */
  allNodeIds?: string[]
}

const KNOWN_ROOTS = new Set(['functions', 'variables', 'trigger', 'nodes', 'data'])
const FUNCTION_CALL_RE = /^functions\.([A-Za-z_][A-Za-z0-9_]*)\s*\(/

/**
 * Yields every ``{{ … }}`` pair in the text. Closed pairs report their
 * inner content + index range; unclosed openers report ``closed: false``
 * with ``end`` pointing at ``text.length``. Brace-depth tracking matches
 * ``findExpressionSpanAt`` so the two stay in agreement on where one
 * expression ends and the next begins.
 */
const forEachSpan = (
  text: string,
  visit: (span: { openIdx: number; closeIdx: number; inner: string; closed: boolean }) => void,
): void => {
  let cursor = 0
  while (cursor < text.length) {
    const openIdx = text.indexOf('{{', cursor)
    if (openIdx === -1) return
    let depth = 1
    let scan = openIdx + 2
    let closeIdx = -1
    while (scan < text.length - 1) {
      // eslint-disable-next-line security/detect-object-injection
      if (text[scan] === '{' && text[scan + 1] === '{') {
        depth += 1
        scan += 2
        continue
      }
      // eslint-disable-next-line security/detect-object-injection
      if (text[scan] === '}' && text[scan + 1] === '}') {
        depth -= 1
        if (depth === 0) {
          closeIdx = scan
          break
        }
        scan += 2
        continue
      }
      scan += 1
    }
    if (closeIdx === -1) {
      visit({
        openIdx,
        closeIdx: text.length,
        inner: text.slice(openIdx + 2),
        closed: false,
      })
      return
    }
    visit({
      openIdx,
      closeIdx,
      inner: text.slice(openIdx + 2, closeIdx),
      closed: true,
    })
    cursor = closeIdx + 2
  }
}

/**
 * Main entry. Returns issues for one text value. Empty array = clean.
 */
export const validateExpressionText = (
  text: string,
  ctx: ValidationContext,
): ValidationIssue[] => {
  if (!text || !text.includes('{{')) return []

  const issues: ValidationIssue[] = []
  forEachSpan(text, ({ openIdx, closeIdx, inner, closed }) => {
    if (!closed) {
      issues.push({
        code: 'unclosed_braces',
        messageKey: 'workflow.validation.unclosedBraces',
        start: openIdx,
        end: closeIdx,
        severity: 'error',
      })
      return
    }

    const end = closeIdx + 2
    const trimmed = inner.trim()
    if (!trimmed) {
      issues.push({
        code: 'empty_expression',
        messageKey: 'workflow.validation.emptyExpression',
        start: openIdx,
        end,
        severity: 'warning',
      })
      return
    }

    issues.push(...validateExpressionBody(trimmed, openIdx, end, ctx))
  })
  return issues
}

/**
 * Validate the *inside* of a closed ``{{ … }}`` span. Already trimmed.
 * Returns 0..N issues; positions point at the surrounding braces so the
 * UI can highlight the whole pair.
 */
const validateExpressionBody = (
  expr: string,
  start: number,
  end: number,
  ctx: ValidationContext,
): ValidationIssue[] => {
  // Function-call form first — it's the only expression with parentheses
  // and a fixed prefix, so check it before the generic path-walker.
  if (expr.startsWith('functions.')) {
    return validateFunctionCall(expr, start, end, ctx)
  }

  // Path walker for the other roots. We split on `.` and check each segment
  // is non-empty; empty segments come from typos like `nodes..foo` or a
  // trailing `trigger.` mid-edit.
  if (expr.endsWith('.') || expr.startsWith('.') || expr.includes('..')) {
    return [
      {
        code: 'empty_segment',
        messageKey: 'workflow.validation.emptySegment',
        start,
        end,
        severity: 'error',
      },
    ]
  }

  const parts = expr.split('.')
  const root = parts[0]

  // Loop variables: declared via parent loop nodes' ``itemVariable``.
  // If the first segment matches one, the rest is free-form (loop items
  // can be any shape) — accept and move on.
  if (ctx.loopVariableNames.includes(root)) return []

  if (!KNOWN_ROOTS.has(root)) {
    return [
      {
        code: 'unknown_root',
        messageKey: 'workflow.validation.unknownRoot',
        messageArgs: { root },
        start,
        end,
        severity: 'error',
      },
    ]
  }

  if (root === 'variables') {
    const name = parts[1]
    if (!name) {
      return [
        {
          code: 'empty_segment',
          messageKey: 'workflow.validation.emptySegment',
          start,
          end,
          severity: 'error',
        },
      ]
    }
    if (!ctx.variableNames.includes(name)) {
      // Variables auto-sync server-side on save, so a missing name now is
      // just an FYI, not a blocker.
      return [
        {
          code: 'unknown_variable',
          messageKey: 'workflow.validation.unknownVariable',
          messageArgs: { name },
          start,
          end,
          severity: 'warning',
        },
      ]
    }
    return []
  }

  if (root === 'trigger') {
    const key = parts[1]
    if (!key) {
      return [
        {
          code: 'empty_segment',
          messageKey: 'workflow.validation.emptySegment',
          start,
          end,
          severity: 'error',
        },
      ]
    }
    // Skip when no schema is known — the workflow has no MCP trigger or
    // the schema is empty. Don't false-flag.
    if (ctx.triggerKeys.length === 0) return []
    if (!ctx.triggerKeys.includes(key)) {
      // Severity: ``warning`` rather than ``error`` because the validator
      // sees the union of trigger keys across *all* trigger nodes in a
      // multi-trigger workflow, not just the keys for the entry-trigger
      // that will actually feed this node at runtime. With disjoint
      // schemas, a typo can match another trigger's key and silently pass.
      // Treating "unknown" as a warning avoids the inverse false-positive
      // until per-entry-trigger narrowing lands (v1 follow-up).
      return [
        {
          code: 'unknown_trigger_key',
          messageKey: 'workflow.validation.unknownTriggerKey',
          messageArgs: { key },
          start,
          end,
          severity: 'warning',
        },
      ]
    }
    return []
  }

  if (root === 'nodes' || root === 'data') {
    const id = parts[1]
    if (!id) {
      return [
        {
          code: 'empty_segment',
          messageKey: 'workflow.validation.emptySegment',
          start,
          end,
          severity: 'error',
        },
      ]
    }
    // When the caller passes ``allNodeIds`` (save-time), distinguish
    // "node doesn't exist" from "node exists but isn't a predecessor".
    // Per-field callers only pass ``availableNodeIds`` so both collapse
    // into ``unknown_node``.
    const reachable = ctx.availableNodeIds.includes(id)
    if (reachable) return []
    const exists = ctx.allNodeIds ? ctx.allNodeIds.includes(id) : false
    if (exists) {
      return [
        {
          code: 'node_not_reachable',
          messageKey: 'workflow.validation.nodeNotReachable',
          messageArgs: { id },
          start,
          end,
          severity: 'error',
        },
      ]
    }
    return [
      {
        code: 'unknown_node',
        messageKey: 'workflow.validation.unknownNode',
        messageArgs: { id },
        start,
        end,
        severity: 'error',
      },
    ]
  }

  return []
}

const validateFunctionCall = (
  expr: string,
  start: number,
  end: number,
  ctx: ValidationContext,
): ValidationIssue[] => {
  const match = FUNCTION_CALL_RE.exec(expr)
  if (!match) {
    return [
      {
        code: 'malformed_function_call',
        messageKey: 'workflow.validation.malformedFunctionCall',
        start,
        end,
        severity: 'error',
      },
    ]
  }
  // Skip name check when the registry hasn't loaded — we don't want a
  // transient false positive while the page is still warming up.
  if (ctx.functionNames === null) return []
  const name = match[1]
  if (!ctx.functionNames.includes(name)) {
    return [
      {
        code: 'unknown_function',
        messageKey: 'workflow.validation.unknownFunction',
        messageArgs: { name },
        start,
        end,
        severity: 'error',
      },
    ]
  }
  return []
}
